/**
 * Display helpers for a mix design's maturity equation (its Nurse-Saul or
 * Arrhenius group from Concrete Maturity Inputs). Shared by the mix design
 * list and the mix design Details card.
 */

export interface MaturityEquationFields {
  maturity_method?: string | null;
  maturity_group_id?: string | null;
  maturity_group_description?: string | null;
  maturity_datum_temp?: number | null;
  maturity_q?: number | null;
}

/** "Nurse Saul: TTF (ASTM C1074), datum 0 °C"; empty when the mix has no group. */
export const maturityEquationName = (p: MaturityEquationFields): string => {
  const name = (p.maturity_group_description || p.maturity_group_id || '').trim();
  if (!name) return '';
  return p.maturity_method ? `${p.maturity_method}: ${name}` : name;
};

/** "Datum 0.0 °C" (Nurse-Saul) or "Q 5000 K" (Arrhenius); empty when unknown. */
export const maturityDatumOrQ = (p: MaturityEquationFields): string => {
  if (p.maturity_datum_temp != null) return `Datum ${p.maturity_datum_temp.toFixed(1)} °C`;
  if (p.maturity_q != null) return `Q ${p.maturity_q} K`;
  return '';
};

/**
 * The equation's constants in full, for the mix design Details card:
 * "Datum 0.0 °C" (Nurse-Saul) or "Q 5000 K, AE 41550 J/mol, Ts 20 °C"
 * (Arrhenius). The list keeps the short maturityDatumOrQ.
 */
export const maturityConstantsDetail = (
  p: MaturityEquationFields & { maturity_activation_energy?: number | null; maturity_specified_temp?: number | null },
): string => {
  if (p.maturity_datum_temp != null) return maturityDatumOrQ(p);
  const parts: string[] = [];
  if (p.maturity_q != null) parts.push(`Q ${p.maturity_q} K`);
  if (p.maturity_activation_energy != null) parts.push(`AE ${p.maturity_activation_energy} J/mol`);
  if (p.maturity_specified_temp != null) parts.push(`Ts ${p.maturity_specified_temp} °C`);
  return parts.join(', ');
};
